import { useEffect, useMemo, useState } from "react";
import { MapPin, Car, Sparkles, Clock, MessageCircle, ClipboardCheck, PackageSearch } from "lucide-react";
import { api } from "@/lib/api";
import { PageHead, EmptyState } from "@/components/Bits";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const StatCard = ({ label, value, icon: Icon, testid }) => (
  <Card className="border-brand-line shadow-none" data-testid={testid}>
    <CardContent className="flex items-center gap-4 p-5">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
        <Icon className="h-5 w-5" strokeWidth={1.8} />
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
        <p className="text-2xl font-bold text-brand-ink">{value}</p>
      </div>
    </CardContent>
  </Card>
);

const BreakdownCard = ({ title, icon: Icon, rows, testid, emptyHint }) => {
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0) || 1;
  const total = rows.reduce((s, r) => s + r.count, 0);
  return (
    <Card className="border-brand-line shadow-none" data-testid={testid}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Icon className="h-4 w-4 text-brand" strokeWidth={1.8} /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.length === 0 ? (
          <EmptyState icon={Icon} title="No data yet" hint={emptyHint} testid={`${testid}-empty`} />
        ) : (
          rows.map((r) => {
            const pct = Math.round((r.count / total) * 100);
            return (
              <div key={r.label} data-testid={`${testid}-row-${r.label}`}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="font-medium text-brand-ink">{r.label}</span>
                  <span className="text-slate-500">
                    {r.count} <span className="text-slate-400">· {pct}%</span>
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-brand-mist">
                  <div
                    className="h-full rounded-full bg-brand transition-all"
                    style={{ width: `${Math.max((r.count / max) * 100, 4)}%` }}
                  />
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};

const PERIODS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "all", label: "All time" },
  { value: "custom", label: "Custom range" },
];

const ymd = (d) => {
  const t = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return t.toISOString().slice(0, 10);
};

const periodRange = (period, from, to) => {
  const now = new Date();
  if (period === "today") return { start: ymd(now), end: ymd(now) };
  if (period === "week") {
    const d = new Date(now);
    const day = (d.getDay() + 6) % 7; // Monday = 0
    d.setDate(d.getDate() - day);
    return { start: ymd(d), end: ymd(now) };
  }
  if (period === "month") {
    const d = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: ymd(d), end: ymd(now) };
  }
  if (period === "custom") return { start: from || null, end: to || null };
  return { start: null, end: null }; // all time
};

export default function Statistics() {
  const [data, setData] = useState(null);
  const [ops, setOps] = useState(null);
  const [studioFilter, setStudioFilter] = useState("all");
  const [period, setPeriod] = useState("month");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [error, setError] = useState(false);

  useEffect(() => {
    const { start, end } = periodRange(period, from, to);
    if (period === "custom" && (!start || !end)) return;
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    setError(false);
    Promise.all([
      api.get("/analytics/whatsapp-enquiries", { params }),
      api.get("/analytics/studio-ops", { params }),
    ])
      .then(([wa, so]) => { setData(wa.data); setOps(so.data); })
      .catch(() => setError(true));
  }, [period, from, to]);

  const opsStudios = ops?.studios || [];
  const selected = useMemo(
    () => opsStudios.find((s) => s.studio_id === studioFilter),
    [opsStudios, studioFilter]
  );

  // Received & above: per-studio totals (All) or the picked studio's service breakdown.
  const receivedRows = useMemo(() => {
    if (studioFilter === "all")
      return opsStudios
        .map((s) => ({ label: s.studio_name, count: s.received_total }))
        .filter((r) => r.count > 0)
        .sort((a, b) => b.count - a.count);
    return selected?.services || [];
  }, [studioFilter, opsStudios, selected]);

  // SKU purchases this month: per-studio totals (All) or the picked studio's SKU breakdown.
  const skuRows = useMemo(() => {
    if (studioFilter === "all")
      return opsStudios
        .map((s) => ({ label: s.studio_name, count: s.sku_total }))
        .filter((r) => r.count > 0)
        .sort((a, b) => b.count - a.count);
    return selected?.sku || [];
  }, [studioFilter, opsStudios, selected]);

  if (error) return <p className="p-6 text-sm text-slate-500">Could not load statistics.</p>;
  if (!data || !ops) return <p className="p-6 text-sm text-slate-500">Loading statistics…</p>;

  const receivedStage = ops.received_from_stage || "Received";

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="statistics-page">
      <PageHead
        title="Statistic Analysis"
        subtitle="Insights from inbound WhatsApp API enquiries — where leads come from and what they drive"
      />

      <div className="mb-6 flex flex-wrap items-center gap-3" data-testid="stat-period-bar">
        <span className="text-sm font-medium text-slate-500">Time period</span>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="min-h-[44px] w-[180px]" data-testid="stat-period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {period === "custom" && (
          <div className="flex items-center gap-2">
            <Input type="date" className="min-h-[44px] w-[160px]" data-testid="stat-period-from"
              value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            <span className="text-slate-400">to</span>
            <Input type="date" className="min-h-[44px] w-[160px]" data-testid="stat-period-to"
              value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
          </div>
        )}
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total WhatsApp enquiries" value={data.total_enquiries} icon={MessageCircle} testid="stat-total" />
        <StatCard label="Qualified (answered)" value={data.qualified} icon={Sparkles} testid="stat-qualified" />
        <StatCard label="Hot leads" value={data.hot} icon={Sparkles} testid="stat-hot" />
        <StatCard label="Cars already delivered" value={data.delivered_cars} icon={Car} testid="stat-delivered" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <BreakdownCard
          title="Enquiries by location"
          icon={MapPin}
          rows={data.by_location}
          testid="stat-location"
          emptyHint="Locations customers picked in the WhatsApp qualification flow will appear here."
        />
        <BreakdownCard
          title="Cars by brand"
          icon={Car}
          rows={data.by_brand}
          testid="stat-brand"
          emptyHint="Car brands customers picked in the WhatsApp qualification flow will appear here."
        />
        <BreakdownCard
          title="Enquiries by service"
          icon={Sparkles}
          rows={data.by_service}
          testid="stat-service"
          emptyHint="Services customers asked about will appear here."
        />
        <BreakdownCard
          title="Enquiries by timeline"
          icon={Clock}
          rows={data.by_timeline}
          testid="stat-timeline"
          emptyHint="When customers plan to get the work done will appear here."
        />
      </div>

      <div className="mt-8 mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-brand-ink">Studio operations</h2>
        <Select value={studioFilter} onValueChange={setStudioFilter}>
          <SelectTrigger className="min-h-[44px] w-[220px]" data-testid="stat-studio-filter">
            <SelectValue placeholder="All studios" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All studios</SelectItem>
            {opsStudios.map((s) => (
              <SelectItem key={s.studio_id} value={s.studio_id}>{s.studio_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        <StatCard
          label={studioFilter === "all" ? `Jobs at ${receivedStage} or above (network)` : `${selected?.studio_name || ""} — ${receivedStage} & above`}
          value={studioFilter === "all" ? ops.received_total_network : (selected?.received_total || 0)}
          icon={ClipboardCheck}
          testid="stat-received-total"
        />
        <StatCard
          label={studioFilter === "all" ? `SKUs purchased this month (network)` : `${selected?.studio_name || ""} — SKUs this month`}
          value={studioFilter === "all" ? ops.sku_total_network : (selected?.sku_total || 0)}
          icon={PackageSearch}
          testid="stat-sku-total"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <BreakdownCard
          title={studioFilter === "all"
            ? `Jobs at ${receivedStage} & above — by studio`
            : `Services opted (${receivedStage} & above)`}
          icon={ClipboardCheck}
          rows={receivedRows}
          testid="stat-received"
          emptyHint={studioFilter === "all"
            ? "Studios with B2C jobs at the Received stage or above will appear here."
            : "This studio has no jobs at the Received stage or above yet."}
        />
        <BreakdownCard
          title={studioFilter === "all"
            ? "Material purchases this month — by studio"
            : "Material purchases this month — by SKU"}
          icon={PackageSearch}
          rows={skuRows}
          testid="stat-sku"
          emptyHint="Received material orders for this month will appear here."
        />
      </div>
    </div>
  );
}
